import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ExportMenu } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import { useSaveFlow } from '../../components/confirm';
import { serverMessage } from '../../components/confirm/serverMessage';
import { UsDateTimeInput } from '../../components/UsDateTimeInput';
import SampleTransferTestAssignment from './SampleTransferTestAssignment';
// The per-test workflow-status list (''· Instrument Down · … · Sub Out · Hold) is the
// SAME shared list Sample Registration's Test View / Test Assignment uses — one source
// of truth so the two forms' Status dropdowns can never drift (owner request 2026-09-29).
import { SAMPLE_STATUS_OPTS } from './sampleRegistration/srData';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';

/**
 * The Samples / Tests / Containers views render in TWO shells:
 *  • `embedded={false}` (default) — the original AppDialog popup.
 *  • `embedded`        — bare body, hosted by <SampleTransferSamplesPage> as a
 *    full-page tab (Sample Registration parity, user request 2026-09-23). A
 *    modal cannot show 48 columns without horizontal scrolling and hides the
 *    transfer form behind it.
 * One `Shell` keeps both paths on the SAME markup, so the grids, editability
 * and toolbars can never drift between popup and page.
 */
export interface PillViewProps {
  jobOid: string;
  /** Human Job ID (260922008). Only needed where a link names the job. */
  jobId?: string;
  onClose: () => void;
  embedded?: boolean;
  /** The owning transfer job is SUBMITTED — samples cannot be deleted (legacy has no
   *  delete-from-a-submitted-transfer flow; owner decision 2026-09-30 "block Delete only"). */
  submitted?: boolean;
  /** Embedded only — lets the hosting page track unsaved grid edits (rule 15). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Embedded only — publishes this panel's Save / Save and Close / Cancel so the
   *  page can render them on the tab row, where Registration puts them. */
  onActionsChange?: (a: PanelActions | null) => void;
}

/** Save trio handed up by a panel to whatever renders its header row. */
export interface PanelActions {
  save: () => void;
  saveAndClose: () => void;
  cancel: () => void;
}

function Shell({ embedded, title, icon, width, onClose, children }: {
  embedded?: boolean; title: string; icon: string; width: number;
  onClose: () => void; children: React.ReactNode;
}) {
  if (embedded) return <div className="stx-pill-page">{children}</div>;
  return (
    <AppDialog open title={title} icon={icon} width={width} maximizable
      onClose={onClose} className="stx-pop-dlg">
      {children}
    </AppDialog>
  );
}

// ─── Types (match the API DTOs) ──────────────────────────────────────────────
export interface SampleLoginRow {
  oid: string; sampleId: string; remark: string; sampleName: string; siteName: string;
  samplingAddress: string; qty: number | null; alternativeStation: string;
  dateCollected: string | null; timeCollected: string; collector: string;
  receivedDate: string | null; sampleStatus: string; systemType: string; sampleMatrix: string;
  collectorPhone: string; pwsid: string; pwsSystemName: string; projectNumber: string; keyMap: string;
  sampleType: string; waterSource: string; previousSample: string; previousCollection: string;
  chlorineFree: string; chlorineTotal: string; rejectionCriteria: string; repeatLocation: string;
  samplingEvent: string; loginBy: string; loginDateTime: string | null; lastUpdatedBy: string;
  lastUpdatedDate: string | null; samplePointID: string; samplePointType: string;
  complianceProject: boolean; type: string;
  sampleNo: number | null; enteredBy: string; enteredDate: string | null; description: string;
  parentSampleID: string; sysSampleCode: string; qcCategory: string; alternativeStationOid: string;
  // Assigned-test count, shown as "(N)" after the SampleID (Registration parity, 2026-09-29).
  testCount: number;
}
export interface TestRow {
  oid: string; sampleOid: string; sampleId: string; matrix: string; test: string; testGroupName: string;
  method: string; components: string;
  // The wire carries `statusIndex` (SampleParameter.[Status] enum); `status` is the
  // resolved label the grid's combo binds to — filled by hydrateTestRows() so the two
  // stay in lock-step with SAMPLE_STATUS_OPTS.
  statusIndex: number; status: string; parameter: string;
  bottleId: string; storage: string; subOut: boolean; remark: boolean; testAddedDate: string | null;
}

/**
 * The `/{jobOid}/tests` reader returns the per-test workflow status as an ENUM index
 * (`statusIndex`), exactly as Sample Registration reads it. Resolve it to its label so
 * the shared editable combo (options = SAMPLE_STATUS_OPTS) can bind to `row.status` —
 * the identical wiring both Registration test grids use.
 */
export function hydrateTestRows(raw: TestRow[]): TestRow[] {
  return raw.map(r => ({ ...r, status: SAMPLE_STATUS_OPTS[r.statusIndex] ?? '' }));
}

/**
 * Persists ONE test's Status cell through Sample Transfer's OWN narrow, receipt-safe
 * endpoint (PUT /test-status/{oid}) — never Registration's job-wide save. Mirrors
 * Registration's onCellCommit: SubOut is derived from the label (`=== 'Sub Out'`), and
 * the label is mapped back to its SAMPLE_STATUS_OPTS index for the wire.
 */
export async function saveTestStatus(sampleParameterOid: string, statusLabel: string): Promise<void> {
  const statusIndex = Math.max(0, SAMPLE_STATUS_OPTS.indexOf(statusLabel));
  const subOut = statusLabel === 'Sub Out';
  await runLoading(
    api.put(`${API_BASE}/test-status/${sampleParameterOid}`, { statusIndex, subOut }),
    { blocking: true, label: 'Saving status…' });
}

/** Sample Registration derives HoldTime client-side from the method (srData.ts
 *  `holdTime`), so the Test Assignment panel here uses the SAME rule rather than
 *  inventing a server-side source Registration does not have. */
export function holdTimeFor(test: string, method: string): string {
  if (test.startsWith('PFAS')) return '27d 22hr 29m';
  if (method === 'SM 9223B') return '30 hr';
  if (method === 'Field Method') return '15 min';
  if (method === 'EPA 300.0') return '48 hr';
  if (method === 'EPA 200.8') return '6 mo';
  return '';
}
export interface ContainerSample { oid: string; sampleId: string; qty: number | null; }
export interface ContainerBottle {
  oid: string; sampleId: string; test: string; bottleId: string; method: string;
  container: string; preservative: string; storageId: string; storageCondition: string;
}
interface ContainersResponse { samples: ContainerSample[]; bottles: ContainerBottle[]; }

const API_BASE = '/api/v1/sample-transfer';

/** Small top search box (legacy popups have one) — filters rows by any visible text. */
function useTextFilter<T>(rows: T[], cols: AppColumn<T>[], q: string): T[] {
  return useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    const fields = cols.map(c => c.field);
    return rows.filter(r =>
      fields.some(f => String((r as Record<string, unknown>)[f] ?? '').toLowerCase().includes(t)));
  }, [rows, cols, q]);
}

function SearchRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="stx-pop-search">
      <span className="tb-i">🔍</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder="Search…" />
      {value && <span className="stx-pop-search-clear" onClick={() => onChange('')}>×</span>}
    </div>
  );
}

/** Reports ▾ dropdown (legacy label reports: JobID / BacT / QR Label). Clicking an item
 *  with NO row selected shows the legacy validation; otherwise it renders the ACTUAL
 *  GoLive label layout (barcode / QR) for the ticked samples and opens the PDF in a new
 *  tab (legacy ShowPdfInNewTab parity), via the shared report engine. The friendly
 *  captions map to their stored report-layout names. */
const REPORT_ITEMS: { label: string; template: string }[] = [
  { label: 'JobID Label', template: 'FolderLabel' },
  { label: 'BacT Label', template: 'SampleReceiving_VerticalBarcode' },
  { label: 'QR Label', template: 'SampleReport_New' },
];
function ReportsMenu({ jobOid, selectedSampleIds }: { jobOid: string; selectedSampleIds: string[] }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const run = async (it: { label: string; template: string }) => {
    setOpen(false);
    if (selectedSampleIds.length === 0) {
      notify.warning('Please select atleast one record to preview the report!');
      return;
    }
    try {
      const res = await runLoading(
        api.get(`${API_BASE}/${jobOid}/sample-label-pdf`, {
          params: { templateName: it.template, sampleIds: selectedSampleIds.join(',') },
          responseType: 'blob',
        }),
        { blocking: true, label: `Generating ${it.label}…` });
      const blob = res.data as Blob;
      if (!blob || blob.size === 0) { notify.info(`No data to print for ${it.label}.`); return; }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { notify.error(`Failed to generate ${it.label}.`); }
  };
  return (
    <span className="stx-pop-reports">
      <button className="stx-tbtn" onClick={() => setOpen(o => !o)}>
        <i className="ti ti-file-text" /> Reports ▾
      </button>
      {open && (
        <>
          <div className="stx-pop-reports-bd" onClick={() => setOpen(false)} />
          <div className="stx-pop-reports-menu" role="menu">
            {REPORT_ITEMS.map(it => (
              <button key={it.template} className="stx-pop-reports-item" role="menuitem" onClick={() => run(it)}>
                <i className="ti ti-printer" /> {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

// ─── Alternative Station (site picker) ────────────────────────────────────────
interface AltStationSite {
  oid: string; siteName: string; address: string; keyMap: string; pwsid: string;
  samplePointID: string; samplePointType: string; waterType: string;
}
/** Opened from the Sample Login AlternativeStation cell. A searchable, MULTI-select
 *  grid of the job client's sites (legacy "Alternative Station" popup); OK joins the
 *  ticked SiteNames (and their Oids) with "; " back onto the sample. */
function AlternativeStationPopup({ jobOid, row, onClose, onPick }:
  { jobOid: string; row: SampleLoginRow;
    onClose: () => void; onPick: (siteNames: string, siteOids: string) => void }) {
  const [sites, setSites] = useState<AltStationSite[]>([]);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let live = true;
    api.get(`${API_BASE}/${jobOid}/alternative-stations`)
      .then(r => {
        if (!live) return;
        const d = (r.data as { data: AltStationSite[] }).data ?? [];
        setSites(d);
        // Preselect by the sample's stored AlternativeStationOid ("; "-joined).
        const preOids = new Set((row.alternativeStationOid || '')
          .split(';').map(s => s.trim().toLowerCase()).filter(Boolean));
        setSel(new Set(d.filter(s => preOids.has(s.oid.toLowerCase())).map(s => s.oid)));
      })
      .catch(() => { if (live) setSites([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [jobOid, row.alternativeStationOid]);

  const cols: AppColumn<AltStationSite>[] = [
    { field: 'siteName', header: 'SiteName', width: 200 },
    { field: 'address', header: 'Address', width: 160 },
    { field: 'keyMap', header: 'KeyMap', width: 90 },
    { field: 'pwsid', header: 'PSWID', width: 110 },
    { field: 'samplePointID', header: 'SamplePointID', width: 130 },
    { field: 'samplePointType', header: 'SamplePointType', width: 135 },
    { field: 'waterType', header: 'WaterType', width: 110 },
  ];
  const filtered = useTextFilter(sites, cols, q);

  const ok = () => {
    const chosen = sites.filter(s => sel.has(s.oid) && s.siteName);
    // Distinct SiteNames + parallel Oids joined with "; " (legacy AcceptAction parity).
    const names = [...new Set(chosen.map(s => s.siteName))].join('; ');
    const oids = [...new Set(chosen.map(s => s.oid))].join('; ');
    onPick(names, oids);
    onClose();
  };

  return (
    <AppDialog open title="Alternative Station" icon="ti-map-pin" width={1000} maximizable onClose={onClose} className="stx-pop-dlg"
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={ok}>OK</button>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      <div className="stx-pop-body">
        <SearchRow value={q} onChange={setQ} />
        <div className="bl-card stx-pop-grid">
          <AppDataGrid<AltStationSite>
            rows={filtered} columns={cols} getRowId={r => r.oid}
            selectable selectedIds={sel} onSelectionChange={setSel}
            loading={loading} emptyText="No sites for this client" />
        </div>
      </div>
    </AppDialog>
  );
}

// ─── Samples ("Sample Login") ────────────────────────────────────────────────
export function SamplesPopup({ jobOid, onClose, embedded, onDirtyChange, onActionsChange }: PillViewProps) {
  const notify = useNotify();
  const saveFlow = useSaveFlow();
  // Wrapper the shared ColumnFinder scrolls (it looks for .adg-scroll inside).
  const gridWrapRef = useRef<HTMLDivElement>(null);
  // The sample MARKED in the grid — drives the Test Assignment panel below.
  const [activeSampleId, setActiveSampleId] = useState<string | null>(null);
  const [rows, setRows] = useState<SampleLoginRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const reqId = useRef(0);

  // Inline edits (legacy: Remark + SampleName are editable text boxes). Tracks which
  // sample rows were changed; Save persists them.
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  // Grid cell editors are INVISIBLE to installUnsavedTracking(), so the hosting
  // page is told explicitly whether there are unsaved edits (standing rule 15).
  // In the popup shell this is a no-op; on the full page it arms the guard.
  useEffect(() => { onDirtyChange?.(Object.keys(dirty).length > 0); }, [dirty, onDirtyChange]);
  // The row whose AlternativeStation cell was clicked → opens the site-picker popup.
  const [altRow, setAltRow] = useState<SampleLoginRow | null>(null);

  // AlternativeStation picker OK → set the joined SiteNames + Oids on the row (dirty).
  const pickAltStation = useCallback((siteNames: string, siteOids: string) => {
    if (!altRow) return;
    const oid = altRow.oid;
    setRows(prev => prev.map(r => (r.oid === oid
      ? { ...r, alternativeStation: siteNames, alternativeStationOid: siteOids } : r)));
    setDirty(prev => ({ ...prev, [oid]: true }));
  }, [altRow]);

  // ComplianceProject is an editable bool (legacy checkbox) — toggle + mark dirty.
  const toggleCompliance = useCallback((oid: string, value: boolean) => {
    setRows(prev => prev.map(r => (r.oid === oid ? { ...r, complianceProject: value } : r)));
    setDirty(prev => ({ ...prev, [oid]: true }));
  }, []);

  const load = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true);
    setDirty({});
    api.get(`${API_BASE}/${jobOid}/samples`)
      .then(r => {
        if (id !== reqId.current) return;
        const data = (r.data as { data: SampleLoginRow[] }).data ?? [];
        setRows(data);
        // Registration opens with the FIRST sample already marked, so Test
        // Assignment is populated on arrival instead of showing a "mark a row"
        // placeholder (user report 2026-09-23). A reload keeps the operator's
        // marked sample when it is still in the job.
        setActiveSampleId(prev => (prev && data.some(d => d.sampleId === prev)
          ? prev
          : data[0]?.sampleId ?? null));
      })
      .catch(() => { if (id === reqId.current) { setRows([]); setActiveSampleId(null); } })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [jobOid]);
  useEffect(() => { load(); }, [load]);

  // Combo option lists for the editable dropdown columns, fetched once per job (Sites
  // are scoped to the job's client).
  const [lookups, setLookups] = useState<{
    collectors: string[]; sampleStatuses: string[]; systemTypes: string[];
    sampleMatrices: string[]; sampleTypes: string[];
    sites: string[]; pwsids: string[]; pwsSystemNames: string[];
    rejectionCriterias: string[]; repeatLocations: string[]; qcCategories: string[];
  }>({ collectors: [], sampleStatuses: [], systemTypes: [], sampleMatrices: [], sampleTypes: [],
       sites: [], pwsids: [], pwsSystemNames: [], rejectionCriterias: [], repeatLocations: [], qcCategories: [] });
  useEffect(() => {
    let live = true;
    api.get(`${API_BASE}/${jobOid}/sample-login-lookups`)
      .then(r => { if (live) setLookups((r.data as { data: typeof lookups }).data); })
      .catch(() => {});
    return () => { live = false; };
  }, [jobOid]);

  const onCellCommit = useCallback((row: SampleLoginRow, col: AppColumn<SampleLoginRow>, v: string) => {
    const val = col.field === 'qty' ? (v.trim() === '' ? null : Number(v)) : v;
    setRows(prev => prev.map(r => (r.oid === row.oid ? { ...r, [col.field]: val } : r)));
    setDirty(prev => ({ ...prev, [row.oid]: true }));
  }, []);

  const saveWith = useCallback(async (after: 'stay' | 'close') => {
    const oids = Object.keys(dirty).filter(o => dirty[o]);
    if (oids.length === 0) { notify.info('No changes to save.'); return; }
    const edits = oids.map(oid => {
      const r = rows.find(x => x.oid === oid)!;
      return {
        oid, remark: r.remark, sampleName: r.sampleName, samplingAddress: r.samplingAddress, qty: r.qty,
        collectorPhone: r.collectorPhone, projectNumber: r.projectNumber, keyMap: r.keyMap,
        waterSource: r.waterSource, previousSample: r.previousSample, previousCollection: r.previousCollection,
        dateCollected: r.dateCollected, timeCollected: r.timeCollected, collector: r.collector, sampleStatus: r.sampleStatus,
        systemType: r.systemType, sampleMatrix: r.sampleMatrix, sampleType: r.sampleType,
        siteName: r.siteName, pwsid: r.pwsid, pwsSystemName: r.pwsSystemName,
        rejectionCriteria: r.rejectionCriteria, repeatLocation: r.repeatLocation,
        chlorineFree: r.chlorineFree, chlorineTotal: r.chlorineTotal, samplingEvent: r.samplingEvent,
        samplePointID: r.samplePointID, samplePointType: r.samplePointType, type: r.type,
        description: r.description, qcCategory: r.qcCategory,
        alternativeStation: r.alternativeStation, alternativeStationOid: r.alternativeStationOid,
        parentSampleID: r.parentSampleID, complianceProject: r.complianceProject,
      };
    });
    // Shared save flow (standing rule 7): reason gate → "Do you want to Save?" →
    // the existing audited PUT → "Saved successfully." It also calls
    // resetUnsavedTracking(), so leaving the page after a save does not warn.
    await saveFlow({
      formName: 'Sample Transfer',
      auditAction: 'edit',
      save: async () => { await api.put(`${API_BASE}/${jobOid}/samples`, { edits }); },
      // `after` decides where the user lands, exactly as Registration's Save vs
      // Save and Close differ only in their after-step (BO UI Standard §4).
      onSaved: () => { setDirty({}); if (after === 'close') onClose(); else load(); },
      // Surface the server's business-rule message verbatim (e.g. the legacy
      // "The test has already been assigned to bottles; please remove the bottle ID."
      // raised when a Qty reduction would orphan an assigned bottle).
      onError: (e) => notify.error(serverMessage(e) ?? 'Failed to save the samples.'),
    });
  }, [dirty, rows, jobOid, notify, load, saveFlow, onClose]);

  const save = useCallback(() => saveWith('stay'), [saveWith]);
  const saveAndClose = useCallback(() => saveWith('close'), [saveWith]);

  /**
   * Hand Save / Save and Close / Cancel UP to the page, which renders them at the
   * top-right of the tab row where Registration puts them (user request
   * 2026-09-23) — this panel owns the dirty state and the save, the page owns
   * that row. Cleared on unmount so a tab without a save shows no buttons.
   */
  useEffect(() => {
    onActionsChange?.({ save, saveAndClose, cancel: load });
    return () => onActionsChange?.(null);
  }, [save, saveAndClose, load, onActionsChange]);

  // Per-column editability mirrors the legacy Sample Login grid (BO SampleLogIn):
  //  • non-editable: SampleID, ReceivedDate, AlternativeStation, LoginBy/DateTime,
  //    LastUpdatedBy/Date, ComplianceProject (read-only bit checkbox).
  //  • text box:  Remark, SampleName, SamplingAddress, CollectorPhone,
  //    ProjectNumber, KeyMap, WaterSource, PreviousSample, PreviousCollection,
  //    FreeColorine, TotalChlorine, SamplingEvent, SamplePointID, SamplePointType, Type.
  //  • number spinner: Qty.  • date: DateCollected.  • time picker: TimeCollected
  //    (a native HH:mm picker, matching Registration — owner report 2026-09-29).
  //  • dropdown (combo): Collector, SampleStatus, SystemType, SampleMatrix, SampleType,
  //    SiteName (client-scoped), PWSID, PWSSystemName, RejectionCriteria, RepeatLocation.
  const cols: AppColumn<SampleLoginRow>[] = [
    // Registration's current-record indicator: the marked row drives the Test
    // Assignment grid below, so the operator must be able to see which one it is.
    {
      field: '__rec', header: '', width: 26, align: 'center',
      sortable: false, filterable: false, noExport: true,
      render: r => (r.sampleId === activeSampleId
        ? <span style={{ color: 'var(--brand-accent, var(--ai-orange))', fontSize: 11 }}>&#9654;</span>
        : null),
    },
    // SampleID with the assigned-test count in brackets, e.g. "260925006-001 (5)",
    // exactly like Sample Registration's Sample Details grid (owner report 2026-09-29).
    { field: 'sampleId', header: 'SampleID', width: 130,
      text: r => `${r.sampleId}${r.testCount > 0 ? ` (${r.testCount})` : ''}`,
      render: r => (
        <span>{r.sampleId}
          {r.testCount > 0 && (
            <span className="sr-hint-inline" style={{ display: 'inline' }}> ({r.testCount})</span>
          )}
        </span>
      ) },
    { field: 'remark', header: 'Remark', width: 110, edit: { type: 'text' } },
    { field: 'sampleName', header: 'SampleName', width: 100, edit: { type: 'text' } },
    { field: 'siteName', header: 'SiteName', width: 130, edit: { type: 'combo', options: lookups.sites } },
    { field: 'samplingAddress', header: 'SamplingAddress', width: 180, edit: { type: 'text' } },
    { field: 'qty', header: 'Qty', width: 70, align: 'right', edit: { type: 'number' } },
    { field: 'alternativeStation', header: 'AlternativeStation', width: 140, sortable: false,
      // The whole cell is clickable → opens the site-picker popup (legacy AlternativeStation
      // cell). No "Select…" caption: an empty cell just opens the popup on click.
      render: r => (
        <div className="stx-cell-click" role="button" tabIndex={0}
          onClick={() => setAltRow(r)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAltRow(r); } }}>
          {r.alternativeStation}
        </div>) },
    { field: 'dateCollected', header: 'DateCollected', width: 115, edit: { type: 'date' },
      text: r => r.dateCollected ? fmtLocalDate(r.dateCollected) : '',
      render: r => r.dateCollected ? fmtLocalDate(r.dateCollected) : '' },
    { field: 'timeCollected', header: 'TimeCollected', width: 105, edit: { type: 'time' } },
    { field: 'collector', header: 'Collector', width: 120, edit: { type: 'combo', options: lookups.collectors } },
    { field: 'receivedDate', header: 'ReceivedDate', width: 145,
      text: r => r.receivedDate ? fmtLocalDateTime(r.receivedDate) : '',
      render: r => r.receivedDate ? fmtLocalDateTime(r.receivedDate) : '' },
    { field: 'sampleStatus', header: 'SampleStatus', width: 110, edit: { type: 'combo', options: lookups.sampleStatuses } },
    { field: 'systemType', header: 'SystemType', width: 100, edit: { type: 'combo', options: lookups.systemTypes } },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 100, edit: { type: 'combo', options: lookups.sampleMatrices } },
    { field: 'collectorPhone', header: 'CollectorPhone', width: 120, edit: { type: 'text' } },
    { field: 'pwsid', header: 'PWSID', width: 110, edit: { type: 'combo', options: lookups.pwsids } },
    { field: 'pwsSystemName', header: 'PWSSystemName', width: 150, edit: { type: 'combo', options: lookups.pwsSystemNames } },
    { field: 'projectNumber', header: 'ProjectNumber', width: 120, edit: { type: 'text' } },
    { field: 'keyMap', header: 'KeyMap', width: 90, edit: { type: 'text' } },
    { field: 'sampleType', header: 'SampleType', width: 100, edit: { type: 'combo', options: lookups.sampleTypes } },
    { field: 'waterSource', header: 'WaterSource', width: 110, edit: { type: 'text' } },
    { field: 'previousSample', header: 'PreviousSample', width: 120, edit: { type: 'text' } },
    { field: 'previousCollection', header: 'PreviousCollection', width: 130, edit: { type: 'text' } },
    { field: 'chlorineFree', header: 'FreeColorine(mg/L)', width: 140, edit: { type: 'text' } },
    { field: 'chlorineTotal', header: 'TotalChlorine(mg/L)', width: 145, edit: { type: 'text' } },
    { field: 'rejectionCriteria', header: 'RejectionCriteria#', width: 140, edit: { type: 'combo', options: lookups.rejectionCriterias } },
    { field: 'repeatLocation', header: 'RepeatLocation', width: 130, edit: { type: 'combo', options: lookups.repeatLocations } },
    { field: 'samplingEvent', header: 'SamplingEvent', width: 120, edit: { type: 'text' } },
    { field: 'loginBy', header: 'LoginBy', width: 100 },
    { field: 'loginDateTime', header: 'LoginDateTime', width: 145,
      text: r => r.loginDateTime ? fmtLocalDateTime(r.loginDateTime) : '',
      render: r => r.loginDateTime ? fmtLocalDateTime(r.loginDateTime) : '' },
    { field: 'lastUpdatedBy', header: 'LastUpdatedBy', width: 115 },
    { field: 'lastUpdatedDate', header: 'LastUpdatedDate', width: 120,
      text: r => r.lastUpdatedDate ? fmtLocalDate(r.lastUpdatedDate) : '',
      render: r => r.lastUpdatedDate ? fmtLocalDate(r.lastUpdatedDate) : '' },
    { field: 'samplePointID', header: 'SamplePointID', width: 130, edit: { type: 'text' } },
    { field: 'samplePointType', header: 'SamplePointType', width: 135, edit: { type: 'text' } },
    { field: 'complianceProject', header: 'ComplianceProject', width: 130, align: 'center', sortable: false,
      text: r => (r.complianceProject ? 'Yes' : 'No'),
      // Editable bool (legacy checkbox) — toggling marks the row dirty for Save.
      render: r => <input type="checkbox" checked={!!r.complianceProject}
        onClick={e => e.stopPropagation()}
        onChange={e => toggleCompliance(r.oid, e.target.checked)} /> },
    { field: 'type', header: 'Type', width: 100, edit: { type: 'text' } },
    { field: 'sampleNo', header: 'SampleNo', width: 90, align: 'right' },
    { field: 'enteredBy', header: 'EnteredBy', width: 110 },
    { field: 'enteredDate', header: 'EnteredDate', width: 120,
      text: r => r.enteredDate ? fmtLocalDate(r.enteredDate) : '',
      render: r => r.enteredDate ? fmtLocalDate(r.enteredDate) : '' },
    { field: 'description', header: 'Description', width: 160, edit: { type: 'text' } },
    { field: 'parentSampleID', header: 'ParentSampleID', width: 130, edit: { type: 'text' } },
    { field: 'sysSampleCode', header: 'SysSampleCode', width: 130 },
    { field: 'qcCategory', header: 'QCCategory', width: 120, edit: { type: 'combo', options: lookups.qcCategories } },
  ];
  // No row filter on this grid any more — the column finder is the search here.
  const filtered = rows;
  // ── Add Sample / Delete (in place, owner request 2026-09-23) ──────────────
  // Both reuse Sample Registration's OWN server endpoints rather than a second
  // implementation (rule 8): they already carry the race-safe numbering, the
  // delete guards and the audit rows, and they keep bottles consistent — which
  // a transfer screen cannot afford to get wrong.
  //
  // The sample acted on is the MARKED (▶) row, exactly as in Registration, whose
  // Add Sample seeds from samples[active] and whose Delete removes samples[active].
  const marked = useMemo(
    () => rows.find(r => r.sampleId === activeSampleId) ?? null, [rows, activeSampleId]);



  // Selected SampleIDs (for the Reports ▾ label reports — legacy filters by SampleID).
  const selectedSampleIds = useMemo(
    () => rows.filter(r => sel.has(r.oid)).map(r => r.sampleId), [rows, sel]);

  return (
    <Shell embedded={embedded} title="Samples" icon="ti-flask" width={1180} onClose={onClose}>
      <div className="stx-pop-body">
        {/* Toolbar (user request 2026-09-23): read actions on the LEFT
            (Reports · Refresh · Export to), record actions on the RIGHT
            (Save · Cancel). No row-search and no Find-column row — both were
            removed on request. */}
        {/* Two boxes, one grid each, inside the one outer card — Registration's
            .sr-sl-panel split (user request 2026-09-23). Box 1 = toolbar + the
            Sample Login grid, box 2 = Test Assignment. */}
        <div className="stx-sl-panel stx-sl-samples">
        <div className="stx-pop-toolbar">
          {/* Legacy Sample Transfer's sample views are AllowNew=False / AllowDelete=False
              (Model.xafml SampleBottleAllocation_ListView_SampleTransfer) — inline edit
              only. So the toolbar is just Reports · Refresh · Export to; the record
              actions (Add/Delete/Copy/Test Edit/Reanalysis) are NOT part of Sample
              Transfer and were removed (owner report 2026-09-30). Save/Save & Close/
              Cancel live on the page's top-right. */}
          <ReportsMenu jobOid={jobOid} selectedSampleIds={selectedSampleIds} />
          <button className="stx-tbtn" onClick={load}><i className="ti ti-refresh" /> Refresh</button>
          <ExportMenu triggerClassName="stx-tbtn" filename={`SampleLogin_${jobOid}`} {...gridExportMatrix(filtered, cols)} />
        </div>
        <div className="stx-pop-grid" ref={gridWrapRef}>
          <AppDataGrid<SampleLoginRow>
            rows={filtered} columns={cols} getRowId={r => r.oid}
            selectable selectedIds={sel} onSelectionChange={setSel}
            onCellCommit={onCellCommit} stickyColumns={2}
            defaultPageSize={100}
            maximizable exportFilename={`SampleLogin_${jobOid}`}
            onRowClick={r => setActiveSampleId(r.sampleId)}
            loading={loading} emptyText="No samples for this job" />
        </div>
        </div>{/* end stx-sl-samples */}
        {/* Second grid — Test Assignment for the marked sample (Sample Registration
            parity, user request 2026-09-23). Page shell only: it needs the height. */}
        {embedded && (
          <div className="stx-sl-panel stx-sl-tests">
            <SampleTransferTestAssignment jobOid={jobOid} sampleId={activeSampleId}
              sampleOid={marked?.oid ?? null} />
          </div>
        )}
      </div>
      {altRow && (
        <AlternativeStationPopup jobOid={jobOid} row={altRow}
          onClose={() => setAltRow(null)} onPick={pickAltStation} />
      )}
    </Shell>
  );
}

// ─── Parameter picker (mirrors Registration's ParameterPickerDialog) ──────────
interface ParamOption {
  oid: string; parameterName: string; checked: boolean;
  isFieldTest: boolean; result: string; units: string; analysisDate: string; analyzedBy: string;
}
/** Per-analyte on-site result scratch for a FIELD method (UI-only — see below). */
interface FieldVals { result: string; units: string; analysisDate: string; analyzedBy: string; }

const PARAM_CELL: CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: '1px solid var(--ai-line)',
  borderRadius: 3, padding: '3px 6px', fontSize: 12,
};

/**
 * Parameter-cell popup — MIRRORS Sample Registration's ParameterPickerDialog (owner
 * request 2026-09-29, UI-only). A checked-list of every eligible parameter for the
 * test + component; ticking / unticking + Apply creates / removes the sample's
 * SampleParameter rows through Sample Transfer's OWN receipt-safe PUT /test-parameters.
 *
 * For a FIELD method it also shows the Result / Units / AnalysisDate / AnalyzedBy
 * columns, pre-filled from any value already entered in Field Data Entry. They are
 * editable exactly like Registration, but Sample Transfer does NOT persist them from
 * here — Registration's own registration-save discards them too, and the real results
 * are entered in Field Data Entry. Only the checklist is saved.
 */
export function ParameterPopup({ row, onClose, onSaved }:
  { row: TestRow; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const [opts, setOpts] = useState<ParamOption[]>([]);
  const [sel, setSel] = useState<Set<string | number>>(new Set());  // checked parameter oids
  const [vals, setVals] = useState<Record<string, FieldVals>>({}); // per-oid field-method scratch
  const [units, setUnits] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');                         // parameter filter box

  const field = !!opts[0]?.isFieldTest;

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`${API_BASE}/test-parameters/${row.oid}`)
      .then(r => {
        if (!live) return;
        const d = (r.data as { data: ParamOption[] }).data ?? [];
        setOpts(d);
        setSel(new Set(d.filter(x => x.checked).map(x => x.oid)));
        const seed: Record<string, FieldVals> = {};
        for (const o of d) seed[o.oid] = { result: o.result, units: o.units, analysisDate: o.analysisDate, analyzedBy: o.analyzedBy };
        setVals(seed);
      })
      .catch(() => { if (live) setOpts([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [row.oid]);

  // Unit names for the Units dropdown — only fetched for a field method.
  useEffect(() => {
    if (!field) return;
    let live = true;
    api.get('/api/v1/sample-registration/lookups')
      .then(r => {
        if (!live) return;
        const rows = (r.data as { data?: { lookupKey: string; col1?: string }[] }).data ?? [];
        setUnits(rows.filter(x => x.lookupKey === 'unit').map(x => x.col1 ?? '').filter(Boolean));
      })
      .catch(() => { /* dropdown falls back to a text input */ });
    return () => { live = false; };
  }, [field]);

  const on = sel.size;
  // Search filters the visible parameter rows (legacy's Parameter popup has a search box).
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? opts.filter(o => o.parameterName.toLowerCase().includes(q)) : opts;
  }, [opts, search]);
  const setField = (oid: string, k: keyof FieldVals, v: string) =>
    setVals(m => ({ ...m, [oid]: { ...(m[oid] ?? { result: '', units: '', analysisDate: '', analyzedBy: '' }), [k]: v } }));

  const ok = async () => {
    if (sel.size === 0) { notify.warning('Select atleast one parameter!'); return; }
    try {
      await runLoading(
        api.put(`${API_BASE}/test-parameters/${row.oid}`, { selectedOids: [...sel] }),
        { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');
      onSaved();
      onClose();
    } catch { notify.error('Failed to save the parameters.'); }
  };

  // Grid columns — a proper <AppDataGrid> (sortable "Parameter" header, checkboxes,
  // pager), matching legacy's Parameter popup (owner report 2026-09-30). Field methods
  // add the on-site Result / Units / AnalysisDate / AnalyzedBy cells.
  const paramCols: AppColumn<ParamOption>[] = [
    { field: 'parameterName', header: 'Parameter', width: field ? 200 : 340 },
    ...(field ? ([
      { field: 'result', header: 'Result', width: 100, sortable: false,
        render: (o: ParamOption) => <input value={vals[o.oid]?.result ?? ''} aria-label={`${o.parameterName} result`}
          onChange={e => setField(o.oid, 'result', e.target.value)} style={PARAM_CELL} /> },
      { field: 'units', header: 'Units', width: 120, sortable: false,
        render: (o: ParamOption) => (units.length ? (
          <select value={vals[o.oid]?.units ?? ''} className="sr-unit-sel" aria-label={`${o.parameterName} units`}
            onChange={e => setField(o.oid, 'units', e.target.value)} style={PARAM_CELL}>
            <option value="">N/A</option>{units.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        ) : (
          <input value={vals[o.oid]?.units ?? ''} aria-label={`${o.parameterName} units`}
            onChange={e => setField(o.oid, 'units', e.target.value)} style={PARAM_CELL} />
        )) },
      { field: 'analysisDate', header: 'AnalysisDate', width: 170, sortable: false,
        render: (o: ParamOption) => <UsDateTimeInput mode="datetime" value={vals[o.oid]?.analysisDate ?? ''}
          className="sr-param-dt" onChange={val => setField(o.oid, 'analysisDate', val)} /> },
      { field: 'analyzedBy', header: 'AnalyzedBy', width: 130, sortable: false,
        render: (o: ParamOption) => <input value={vals[o.oid]?.analyzedBy ?? ''} aria-label={`${o.parameterName} analyzed by`}
          onChange={e => setField(o.oid, 'analyzedBy', e.target.value)} style={PARAM_CELL} /> },
    ] as AppColumn<ParamOption>[]) : []),
  ];

  return (
    <AppDialog open title={`${row.test} — parameters`} icon="ti-list-check"
      width={field ? 820 : 520} onClose={onClose} className="stx-pop-dlg"
      footer={<>
        <button className="ca-btn ca-btn-solid" disabled={loading || on < 1} onClick={ok}>OK</button>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--ai-muted)' }}>Loading parameters…</div>
      ) : (
        <div className="stx-pop-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{on} of {opts.length}</span>
            <span className="sr-hint-inline">
              {field
                ? 'Field method — record on-site results below (saved in Field Data Entry)'
                : 'Lab method — results are entered in Result Entry'}
            </span>
            <input className="stx-edit" placeholder="Search parameter…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ marginLeft: 'auto', width: 200 }} aria-label="Search parameter" />
          </div>
          <AppDataGrid<ParamOption>
            rows={shown} columns={paramCols} getRowId={o => o.oid}
            selectable selectedIds={sel}
            onSelectionChange={next => { if (next.size >= 1) setSel(next); }}
            defaultPageSize={20}
            emptyText="No parameters." />
        </div>
      )}
    </AppDialog>
  );
}

// ─── Tests ───────────────────────────────────────────────────────────────────
export function TestsPopup({ jobOid, jobId, onClose, embedded }: PillViewProps) {
  const [rows, setRows] = useState<TestRow[]>([]);
  const [loading, setLoading] = useState(true);
  // The row whose Parameter cell was clicked → opens the checked-list popup (legacy
  // Sample Transfer's Tests popup opens the same Parameter popup — owner report 2026-09-30).
  const [paramRow, setParamRow] = useState<TestRow | null>(null);
  // Row checkboxes match legacy's Tests popup. The Delete button is removed (legacy's
  // Tests popup has no Delete — Export is the only toolbar action), so selection is
  // display-only here.
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    return api.get(`${API_BASE}/${jobOid}/tests`)
      .then(r => setRows(hydrateTestRows((r.data as { data: TestRow[] }).data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [jobOid]);

  useEffect(() => { let live = true; if (live) load(); return () => { live = false; }; }, [load]);

  const cols: AppColumn<TestRow>[] = [
    { field: 'sampleId', header: 'SampleID', width: 130 },
    { field: 'matrix', header: 'Matrix', width: 100 },
    { field: 'test', header: 'Test', width: 200 },
    { field: 'testGroupName', header: 'TestGroupName', width: 140 },
    { field: 'method', header: 'Method', width: 120 },
    { field: 'components', header: 'Components', width: 120 },
    // Status is READ-ONLY here — legacy's Tests popup is a viewer (no Save), so the
    // status is not editable from this tab (owner report 2026-09-30).
    { field: 'status', header: 'Status', width: 130 },
    // The whole Parameter cell is a clickable combo → opens the analyte checked-list
    // popup (legacy TestParams cell). Blue link + ▾, exactly like Registration.
    { field: 'parameter', header: 'Parameter', width: 120, sortable: false,
      render: r => (
        <div className="stx-cell-click" role="button" tabIndex={0}
          style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={() => setParamRow(r)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setParamRow(r); } }}>
          {r.parameter} ▾
        </div>) },
  ];

  return (
    <Shell embedded={embedded} title="Tests" icon="ti-test-pipe" width={1080} onClose={onClose}>
      <div className="stx-pop-body">
        {/* Section caption + Delete + Export to, exactly like Registration's Test
            View sub-toolbar (.sr-subtoolbar / .sr-sublbl pushes the buttons right).
            No top search box — Registration's Test View has none; per-column filters
            in the grid header cover filtering (user request 2026-09-29). */}
        <div className="sr-subtoolbar">
          <span className="sr-sublbl">Tests — all tests per sample</span>
          {/* Legacy's Tests popup has NO Delete — Export is the only toolbar action;
              filename uses the human Job ID, not the internal Oid (owner report 2026-09-30). */}
          <ExportMenu triggerClassName="stx-tbtn" filename={`Tests_${jobId ?? jobOid}`} {...gridExportMatrix(rows, cols)} />
        </div>
        <div className="bl-card stx-pop-grid">
          <AppDataGrid<TestRow>
            rows={rows} columns={cols} getRowId={r => r.oid}
            selectable selectedIds={sel} onSelectionChange={setSel}
            defaultPageSize={100}
            loading={loading} emptyText="No tests for this job" />
        </div>
      </div>
      {paramRow && (
        <ParameterPopup row={paramRow} onClose={() => setParamRow(null)} onSaved={load} />
      )}
    </Shell>
  );
}

// ─── Containers (master-detail) ───────────────────────────────────────────────
/** SUPERSEDED 2026-09-28 - the Containers tab now renders Sample Registration’s own
 *  ContainersTab (see SampleTransferContainersTab.tsx), which has the All in One /
 *  By Sample toggle, editable bottle columns and Copy / Reset Bottles that this
 *  read-only two-grid view never had. Nothing references it any more, so it is
 *  tree-shaken out of the bundle; kept only until GET /sample-transfer/{job}/containers
 *  is retired with it. Do NOT wire it back. */
export function ContainersPopup({ jobOid, onClose, embedded }: PillViewProps) {
  const [samples, setSamples] = useState<ContainerSample[]>([]);
  const [bottles, setBottles] = useState<ContainerBottle[]>([]);
  const [loading, setLoading] = useState(true);
  // Legacy parity: the right grid fills ONLY for the CHECKED sample(s) — nothing is
  // shown until the analyst ticks a checkbox on the left.
  const [checked, setChecked] = useState<Set<string | number>>(new Set());
  // The right (bottles) grid has its OWN row checkboxes too (legacy parity).
  const [bottleSel, setBottleSel] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`${API_BASE}/${jobOid}/containers`)
      .then(r => {
        if (!live) return;
        const d = (r.data as { data: ContainersResponse }).data;
        setSamples(d?.samples ?? []);
        setBottles(d?.bottles ?? []);
        setChecked(new Set());
      })
      .catch(() => { if (live) { setSamples([]); setBottles([]); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [jobOid]);

  const sampleCols: AppColumn<ContainerSample>[] = [
    { field: 'sampleId', header: 'SampleID', width: 140 },
    { field: 'qty', header: 'Qty', width: 70, align: 'right' },
  ];
  const bottleCols: AppColumn<ContainerBottle>[] = [
    { field: 'test', header: 'Test', width: 170 },
    { field: 'bottleId', header: 'BottleID', width: 100 },
    { field: 'method', header: 'Method', width: 120 },
    { field: 'container', header: 'Containers', width: 120 },
    { field: 'preservative', header: 'Preservative', width: 120 },
    { field: 'storageId', header: 'StorageID', width: 110 },
    { field: 'storageCondition', header: 'StorageCondition', width: 130 },
  ];
  // Map the checked sample Oids → their SampleId strings, then filter the bottles.
  const rightRows = useMemo(() => {
    const ids = new Set(samples.filter(s => checked.has(s.oid)).map(s => s.sampleId));
    return ids.size ? bottles.filter(b => ids.has(b.sampleId)) : [];
  }, [samples, bottles, checked]);

  return (
    <Shell embedded={embedded} title="Containers" icon="ti-box" width={1180} onClose={onClose}>
      <div className="stx-pop-body stx-pop-md">
        <div className="bl-card stx-pop-md-left">
          <AppDataGrid<ContainerSample>
            rows={samples} columns={sampleCols} getRowId={r => r.oid}
            selectable selectedIds={checked} onSelectionChange={setChecked}
            loading={loading} emptyText="No samples" />
        </div>
        <div className="bl-card stx-pop-md-right">
          <AppDataGrid<ContainerBottle>
            rows={rightRows} columns={bottleCols} getRowId={r => r.oid}
            selectable selectedIds={bottleSel} onSelectionChange={setBottleSel}
            emptyText="No data to display" />
        </div>
      </div>
    </Shell>
  );
}
